import React, { Dispatch, PropsWithChildren, SetStateAction, useMemo } from 'react';

import { Warning } from '@da-g3-acl/shared/common';

import { StatusBarImageTypesSelector } from './components/StatusBar/StaturBarImageTypesSelector';
import { StatusBarContainer } from './components/StatusBar/StatusBarContainer';
import { StatusBarItemCursorPosition } from './components/StatusBar/StatusBarItemCursorPosition';
import { StatusBarItemLoadingPercentage } from './components/StatusBar/StatusBarItemLoadingPercentage';
import { StatusBarItemRangeStatus } from './components/StatusBar/StatusBarItemRangeStatus';
import { StatusBarItemWarnings } from './components/StatusBar/StatusBarItemWarnings';
import { StatusBarItemZoom } from './components/StatusBar/StatusBarItemZoom';
import { StatusBarMenuSelector, StatusBarMenuSelectorProps } from './components/StatusBar/StatusBarMenuSelector';
import { useCoreViewerContext } from './context/CoreViewerContext';
import { useMappedRowMenuItems } from './hooks/use-core-viewer-statusbar-menus';
import { ImageTypeLoaders } from './types/imageTypeLoader';
import { ImageTypeMenuItem } from './types/imageTypeMenuItem';
import { RowMenuItem } from './types/rowMenuItem';

/**
 * When the parent container height is provided in px, this is a buffer which provides some space above and below the menu container
 */
const PARENT_CONTAINER_BUFFER = 100;

export interface CoreViewerStatusBarProps {
	hover?: number;
	loaders: ImageTypeLoaders;
	imageTypeMenuItems: ImageTypeMenuItem[] | undefined;
	selectedImageMenuItemIds?: number[];
	setSelectedImageTypeMenuItemIds?: Dispatch<SetStateAction<number[]>>;
	imageMenuLoading?: boolean;
	selectedRowMenuItemIds?: string[] | undefined;
	rowMenuLoading?: boolean;
	rowMenuItems?: RowMenuItem[] | undefined;
	onRowMenuItemSelected?: (menuItemId: string) => void;
	parentContainerHeight?: number;
	depthConverter?: (depth: number) => number | undefined;
	warnings?: Warning[];
}

export const CoreViewerStatusBar: React.FC<PropsWithChildren<CoreViewerStatusBarProps>> = (props) => {
	const {
		hover,
		loaders,
		imageTypeMenuItems,
		imageMenuLoading = false,
		selectedImageMenuItemIds,
		setSelectedImageTypeMenuItemIds,
		rowMenuItems,
		rowMenuLoading = false,
		selectedRowMenuItemIds,
		onRowMenuItemSelected,
		parentContainerHeight,
		children,
		depthConverter,
		warnings,
	} = props;

	const { viewportVisibleLanesRange, mergedRange } = useCoreViewerContext();

	const mappedRowMenuItems = useMappedRowMenuItems(rowMenuItems);

	const rowMenuSelector = useMemo<StatusBarMenuSelectorProps | undefined>(() => {
		if (!mappedRowMenuItems) return undefined;
		return {
			selectionTypeName: 'Rows',
			selectedMenuItemIds: selectedRowMenuItemIds,
			menuItems: mappedRowMenuItems,
			onTypeSelected: (id): void => onRowMenuItemSelected && onRowMenuItemSelected?.(id),
			isLoading: rowMenuLoading,
		};
	}, [onRowMenuItemSelected, rowMenuLoading, mappedRowMenuItems, selectedRowMenuItemIds]);

	return (
		<StatusBarContainer>
			{imageTypeMenuItems && (
				<StatusBarImageTypesSelector
					imageTypeMenuItems={imageTypeMenuItems}
					selectedMenuItemIds={selectedImageMenuItemIds}
					setSelectedMenuItemIds={setSelectedImageTypeMenuItemIds}
					loaders={loaders}
					isLoading={imageMenuLoading}
				/>
			)}
			{rowMenuSelector && (
				<StatusBarMenuSelector
					maxHeight={parentContainerHeight ? parentContainerHeight - PARENT_CONTAINER_BUFFER : undefined}
					{...rowMenuSelector}
				/>
			)}
			<StatusBarItemZoom />
			<StatusBarItemRangeStatus
				range={{
					depthFrom:
						depthConverter?.(viewportVisibleLanesRange.depthFrom) ?? viewportVisibleLanesRange.depthFrom,
					depthTo: depthConverter?.(viewportVisibleLanesRange.depthTo) ?? viewportVisibleLanesRange.depthTo,
				}}
				clampTo={mergedRange}
			/>
			<StatusBarItemCursorPosition positionInM={hover ? (depthConverter?.(hover) ?? hover) : undefined} />
			<StatusBarItemLoadingPercentage />
			{children}
			{warnings && warnings.length > 0 && <StatusBarItemWarnings warnings={warnings} />}
		</StatusBarContainer>
	);
};
